import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { IUiSchema } from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { IContactListDataSourceItem } from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import {
  contactSelectInlineV2ValueSelector,
  getValue,
} from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import { ERentioMoveService } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.types";
import {
  IAccount,
  IMember,
} from "@rentiohq/shared-frontend/dist/types/auth.types";
import { EContactInitialType } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { EContractMemberTypes } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { EPropertyMemberTypes } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { JSONSchema6 } from "json-schema";
import { uniqBy } from "lodash";
import { FormValidation } from "react-native-jsonschema-form-test";
import {
  EField,
  IExtraData,
} from "./schema.requestRentioMoveServiceExit.types";

const filterPropertyMembers = (
  state: IPartialRootState,
  members: IMember<EPropertyMemberTypes>[],
) => {
  const allAccounts = members
    .filter(
      member =>
        !(
          member.roles.includes(EPropertyMemberTypes.TaskMember) &&
          member.roles.length === 1
        ),
    )
    .map(member => member.account)
    .filter(account =>
      Object.values(state.contact.data).some(
        contact =>
          contact?.accountId === account.id ||
          contact?.accountIds.includes(account.id),
      ),
    );

  return uniqBy(uniqBy(allAccounts, "id"), x => x.userId || Math.random());
};

// Helper function to check if contract has tenants
const hasTenants = (extraData: IExtraData) =>
  extraData.contract?.members?.some(member =>
    member.roles.includes(EContractMemberTypes.Tenant),
  ) || false;

export const getSchema =
  (initialValues: { [key: string]: any }, extraData: IExtraData) =>
  (defaultValues: { [key: string]: any }): JSONSchema6 => {
    return {
      required: [EField.MoveDate, EField.Services],
      properties: {
        [EField.MoveDate]: {
          default: getValue(EField.MoveDate, initialValues, defaultValues),
        },
        [EField.DepartingTenantTitle]: {
          default: undefined,
          //@ts-ignore
          showIf: (formData: any) => false, // Hidden since DepartingTenant field already has a title
        },
        [EField.DepartingTenantFirstName]: {
          default: getValue(
            EField.DepartingTenantFirstName,
            initialValues,
            defaultValues,
          ),
          //@ts-ignore
          showIf: (formData: any) => false, // Hidden since we use contact card now
          requiredIf: (formData: any) => false, // Not required since we use contact card now
        },
        [EField.DepartingTenantLastName]: {
          default: getValue(
            EField.DepartingTenantLastName,
            initialValues,
            defaultValues,
          ),
          //@ts-ignore
          showIf: (formData: any) => false, // Hidden since we use contact card now
          requiredIf: (formData: any) => false, // Not required since we use contact card now
        },
        [EField.DepartingTenantEmail]: {
          default: getValue(
            EField.DepartingTenantEmail,
            initialValues,
            defaultValues,
          ),
          //@ts-ignore
          showIf: (formData: any) => false, // Hidden since we use contact card now
          requiredIf: (formData: any) => false, // Not required since we use contact card now
        },
        [EField.DepartingTenant]: {
          default: (() => {
            const existingValue = getValue(
              EField.DepartingTenant,
              initialValues,
              defaultValues,
            );
            if (existingValue) return existingValue;

            // Auto-select first tenant when contract has tenants
            if (hasTenants(extraData)) {
              const firstTenant = extraData.contract?.members?.find(member =>
                member.roles.includes(EContractMemberTypes.Tenant),
              );
              return firstTenant ? [firstTenant!.account.id] : undefined;
            }

            return undefined;
          })(),
          //@ts-ignore
          showIf: (formData: any) => hasTenants(extraData),
          requiredIf: (formData: any) => hasTenants(extraData),
        },
        [EField.VacancyPropertyContractHolder]: {
          default: getValue(
            EField.VacancyPropertyContractHolder,
            initialValues,
            defaultValues,
          ),
          //@ts-ignore
          showIf: (formData: any) => true,
        },
        [EField.Services]: {
          default: getValue(EField.Services, initialValues, defaultValues),
          // @ts-ignore
          options: [
            ERentioMoveService.Energy,
            ERentioMoveService.Gas,
            ERentioMoveService.Water,
          ].map(id => ({
            id,
            value: getLocalizedText(`move.option.service.${id}`.toLowerCase()),
            enabledOtherOptionsIfSelected:
              id === ERentioMoveService.Water
                ? [ERentioMoveService.Energy]
                : undefined,
          })),
        },
        [EField.ResidentCount]: {
          default: getValue(EField.ResidentCount, initialValues, defaultValues),
        },
      },
    };
  };

export const uiSchema = (extraData: IExtraData): IUiSchema<EField> => ({
  [EField.MoveDate]: {
    "ui:field": ERentioFormField.Date,
    title: getLocalizedText("move.field.move_date.title"),
  },
  [EField.DepartingTenantTitle]: {
    "ui:field": ERentioFormField.Label,
    title: getLocalizedText("move.field.departing_tenant.title"),
  },
  [EField.DepartingTenantFirstName]: {
    "ui:field": ERentioFormField.Text,
    title: getLocalizedText("system.firstname"),
    placeholder: getLocalizedText("system.firstname"),
    autoFocus: true,
    hideLabel: true,
  },
  [EField.DepartingTenantLastName]: {
    "ui:field": ERentioFormField.Text,
    title: getLocalizedText("system.lastname"),
    placeholder: getLocalizedText("system.lastname"),
    hideLabel: true,
  },
  [EField.DepartingTenantEmail]: {
    "ui:field": ERentioFormField.Email,
    title: getLocalizedText("system.email"),
    placeholder: getLocalizedText("system.email"),
    hideLabel: true,
  },
  [EField.DepartingTenant]: {
    "ui:field": ERentioFormField.ContactSelectInlineV2,
    title: getLocalizedText("move.field.departing_tenant.title"),
    selectContactFromListCtaTitle: getLocalizedText(
      "contract_info.tenants.select_other.cta",
    ),
    addCtaLabel: getLocalizedText("contract_info.add.tenants.heading"),
    isMultiSelect: false,

    prefetchDefault: true,
    initialType: EContactInitialType.Tenant,

    prefilledAddress: (() => {
      const { property } = extraData;
      return {
        street: property.street,
        number: property.number,
        box: property.box,
        zip: property.zip,
        city: property.city,
        state: property.state,
        countryId: property.countryId,
      };
    })(),

    valueSelector: (
      state: IPartialRootState,
      selectedAccountIds?: number[] | string,
    ) =>
      contactSelectInlineV2ValueSelector(
        state,
        selectedAccountIds,
        extraData.property.members.map(member => member.account),
      ),
    customGetSubtitleSelector: (
      _: IPartialRootState,
      __: any,
      item: IContactListDataSourceItem,
    ) => {
      const { accountId, contact } = item;

      const member = extraData.property.members.find(member => {
        if (accountId && accountId === member.account.id) {
          return true;
        }

        if (contact?.accountIds.includes(member.account.id)) {
          return true;
        }

        return false;
      });

      if (!member) {
        return;
      }

      return member.roles
        .filter(
          role =>
            ![
              EPropertyMemberTypes.ContractMember,
              EPropertyMemberTypes.TaskMember,
            ].includes(role),
        )
        .map(role => getLocalizedText(`role.${role.toLowerCase()}`))
        .join(", ");
    },
    customExtraAccountsSelector: (
      state: IPartialRootState,
    ): IAccount[] | undefined => {
      if (extraData.property?.members) {
        return filterPropertyMembers(state, extraData.property?.members);
      }
      return undefined;
    },
    customIsFetchingSelector: () => false,
    customGetContactsActions: [],
  },
  [EField.VacancyPropertyContractHolder]: {
    "ui:field": ERentioFormField.ContactSelectInlineV2,
    title: getLocalizedText(
      "exit_flow.energy_water.field.vacancy_property_contract_holder.title",
    ),
    selectContactFromListCtaTitle: getLocalizedText(
      "contract_info.owners.select_other.cta",
    ),
    addCtaLabel: getLocalizedText("contract_info.add.owners.heading"),
    isMultiSelect: false,

    prefetchDefault: true,
    initialType: EContactInitialType.Owner,

    prefilledAddress: (() => {
      const { property } = extraData;
      return {
        street: property.street,
        number: property.number,
        box: property.box,
        zip: property.zip,
        city: property.city,
        state: property.state,
        countryId: property.countryId,
      };
    })(),

    valueSelector: (
      state: IPartialRootState,
      selectedAccountIds?: number[] | string,
    ) =>
      contactSelectInlineV2ValueSelector(
        state,
        selectedAccountIds,
        extraData.property.members.map(member => member.account),
      ),
    customGetSubtitleSelector: (
      _: IPartialRootState,
      __: any,
      item: IContactListDataSourceItem,
    ) => {
      const { accountId, contact } = item;

      const member = extraData.property.members.find(member => {
        if (accountId && accountId === member.account.id) {
          return true;
        }

        if (contact?.accountIds.includes(member.account.id)) {
          return true;
        }

        return false;
      });

      if (!member) {
        return;
      }

      return member.roles
        .filter(
          role =>
            ![
              EPropertyMemberTypes.ContractMember,
              EPropertyMemberTypes.TaskMember,
            ].includes(role),
        )
        .map(role => getLocalizedText(`role.${role.toLowerCase()}`))
        .join(", ");
    },
    customExtraAccountsSelector: (
      state: IPartialRootState,
    ): IAccount[] | undefined => {
      if (extraData.property?.members) {
        return filterPropertyMembers(state, extraData.property?.members);
      }
      return undefined;
    },
    customIsFetchingSelector: () => false,
    customGetContactsActions: [],
  },
  [EField.Services]: {
    "ui:field": ERentioFormField.InlineSelect,
    title: getLocalizedText("move.field.services.title"),
    isMultiSelect: true,
    asOptionList: true,
  },
  [EField.ResidentCount]: {
    "ui:field": ERentioFormField.Number,
    title: getLocalizedText("move.field.resident_count.title"),
    optional: true,
  },
});

export const getValidate =
  (_: IExtraData) => (formData: any, errors: FormValidation) => {
    const services: ERentioMoveService[] = formData[EField.Services] || [];
    if (services.length === 0) {
      errors[EField.Services].addError(
        getLocalizedText("system.form.error.required.field", {
          field: getLocalizedText("move.field.services.title"),
        }),
      );
    }

    return errors;
  };
